import { createElement, useEffect, useState } from "react";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import { HOME_ROUTE, ROUTES, type Route } from "./routes.ts";

// THE ROUTER. Hash-based, no dependency, and THE ONLY MODULE IN THE APP THAT READS `window.location`.
//
// Two defects are excluded here by construction:
//
//   1. An unmatched hash renders a REAL 404. Falling through to the board for anything unrecognised makes a
//      typo look like a working page — and a link that silently shows the wrong screen is worse than one
//      that says it is broken.
//   2. `navigate` lives HERE, not on the root `App.tsx`. Exporting it from the root module makes every leaf
//      that navigates depend on the component tree's root.
//
// Why not react-router / TanStack Router: their DATA LOADERS are incompatible with the stitch. A loader fetches
// BEFORE the component mounts, therefore before it can subscribe — that is fetch-then-subscribe, which loses 86
// trades permanently per 300 ms of fetch + RTT on the hottest key and 0.0005 on the median mint. Paying ~12 KB
// gz for a model we then have to defeat is the wrong trade.

/** Split a hash into its path segments and its query. PURE — it takes the hash as an argument and reads no
 *  global, which is what makes the whole matcher unit-testable without a DOM. */
export function parseHash(hash: string): { segments: string[]; query: URLSearchParams } {
  const withoutHash = hash.startsWith("#") ? hash.slice(1) : hash;
  const [rawPath = "", rawQuery = ""] = withoutHash.split("?");
  const segments = rawPath.split("/").filter((s) => s.length > 0);
  return { segments, query: new URLSearchParams(rawQuery) };
}

/** Resolve a hash to a route. PURE, total, and never throws: every input resolves to something, and anything
 *  unrecognised resolves to `notfound` rather than to a screen that happens to be first. */
export function matchRoute(hash: string): Route {
  const { segments, query } = parseHash(hash);

  // `#`, `#/`, `#/?x=1` — the empty path. `useRoute` turns this into a redirect to HOME_ROUTE so the address
  // bar names the screen; matching it to `board` here as well would make the redirect unobservable in tests.
  if (segments.length === 0) return { label: "notfound", params: {}, query };

  for (const def of ROUTES) {
    // ARITY FIRST, and it is what makes `#/token/` a 404 rather than a token page with an empty mint:
    // `parseHash` drops empty segments, so a trailing slash is one segment short of the pattern. A page with an
    // empty mint would issue `GET /token//info` and fail at the SERVER instead — one layer further from the
    // cause, and rendered as a market outage rather than as a bad link.
    if (def.pattern.length !== segments.length) continue;
    const params: Record<string, string> = {};
    let matched = true;
    for (let i = 0; i < def.pattern.length; i++) {
      const expected = def.pattern[i] as string;
      const actual = segments[i];
      if (expected.startsWith(":")) {
        // `actual` is `string | undefined` and stays that way: casting the index away here is exactly how the
        // arity check above becomes load-bearing-but-unchecked, and the symptom would be `params.mint ===
        // undefined` reaching a URL as the literal string "undefined".
        if (actual === undefined || actual.length === 0) {
          matched = false;
          break;
        }
        params[expected.slice(1)] = actual;
      } else if (expected !== actual) {
        matched = false;
        break;
      }
    }
    if (matched) return { label: def.label, params, query };
  }

  return { label: "notfound", params: {}, query };
}

/** Navigate. Assigning `location.hash` fires `hashchange`, which is the single subscription below — so there is
 *  one path into a route change and no second way to end up on a screen. */
export function navigate(hash: string): void {
  const next = hash.startsWith("#") ? hash : `#${hash.startsWith("/") ? "" : "/"}${hash}`;
  if (window.location.hash === next) return;
  window.location.hash = next;
}

/** Replace the current entry rather than pushing one — used for the `#/` → home redirect, so Back from the
 *  home screen leaves the app instead of bouncing between `#/` and `#/board` forever. */
export function replace(hash: string): void {
  window.history.replaceState(null, "", hash);
}

function currentHash(): string {
  return window.location.hash || "#/";
}

/** The one subscription to the URL. */
export function useRoute(): Route {
  const [hash, setHash] = useState(currentHash);

  useEffect(() => {
    const onHashChange = () => setHash(currentHash());
    window.addEventListener("hashchange", onHashChange);
    // Read once more on mount: the hash can change between the initial `useState` and the listener being
    // attached (a redirect in another effect, a restored session), and a missed change here strands the app on
    // the wrong screen until the next navigation.
    onHashChange();
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (parseHash(hash).segments.length === 0) {
      replace(HOME_ROUTE);
      setHash(HOME_ROUTE);
    }
  }, [hash]);

  return matchRoute(hash);
}

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
  children?: ReactNode;
}

/** An in-app link. Written with `createElement` rather than JSX so this module stays `router.ts` as the design
 *  prescribes — one module owns the URL, and moving it to `.tsx` for one element would split that ownership
 *  across two files. A hash href needs no click handler: the browser's own navigation fires `hashchange`. */
export function Link({ to, children, ...rest }: LinkProps): ReactNode {
  const href = to.startsWith("#") ? to : `#${to.startsWith("/") ? "" : "/"}${to}`;
  return createElement("a", { href, ...rest }, children);
}
